import { subYears } from 'date-fns/subYears';
import { useState, useCallback, useMemo } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';

import { getDateFromTwelveMonthsAgo, getDateTwelveMonthsInTheFuture } from '.';
import { useConfig } from '../../../defaultConfig';
import type { TransactionCategory, SearchTransactionsRequest } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { arrayOfAll } from '../../../utils/mapper/arrayOfAll';
import nullableZonedDateToUtc from '../../../utils/mapper/nullableZonedDateToUtc';
import { CurrencyChangeParam } from './AmountRangeInput';
import type { MoneyDirection, TransactionFilterFormType } from './transactionFilterTypes';
import getSearchAmounts from './utils/getSearchAmounts';
import { isOneYearFromToday } from './utils/getSearchDates';

const transactionCategoryOptions = arrayOfAll<TransactionCategory>()([
    'INCOMING',
    'OUTGOING',
    'PAYMENT',
    'TRANSFER',
    'INTERNATIONAL',
    'PURCHASE',
    'SALARY',
    'INTEREST',
    'CHARGE',
    'OTHER',
]);

const DEFAULT_FROM_DATE = subYears(getZonedDate(), 1);
const DEFAULT_TO_DATE = getZonedDate();

const useTransactionFilter = (): TransactionFilterFormType => {
    const { featureToggles } = useConfig();
    const translations = useTranslation();

    const [transactionCategories, setTransactionCategories] = useState<TransactionCategory[]>([]);
    const [freetext, setFreetext] = useState<string>();
    const [moneyDirection, setMoneyDirection] = useState<MoneyDirection>('sent');
    const [minAmount, setMinAmount] = useState<number>();
    const [maxAmount, setMaxAmount] = useState<number>();
    const [fromDate, setFromDate] = useState<ZonedDate | undefined>(DEFAULT_FROM_DATE);
    const [toDate, setToDate] = useState<ZonedDate | undefined>(DEFAULT_TO_DATE);

    const isFilteringState = useState(false);
    const [, setIsFiltering] = isFilteringState;

    const isDirty = useMemo(() => {
        return (
            !isOneYearFromToday(fromDate) ||
            typeof minAmount === 'number' ||
            typeof maxAmount === 'number' ||
            Boolean(freetext) ||
            transactionCategories.length > 0
        );
    }, [fromDate, maxAmount, minAmount, freetext, transactionCategories]);

    const [categoryWarningMessage, setCategoryWarningMessage] = useState<string>();
    const [freetextWarning, setFreetextWarning] = useState<string>();

    const allCategories = useMemo(
        () =>
            transactionCategoryOptions.map((key) => ({
                value: key,
                label: translations.transactionCategories[key],
            })),
        [translations.transactionCategories],
    );

    const defineTransactionCategories = useCallback(
        (checked: boolean, value: string) => {
            const targetCategory = allCategories.find((cat) => cat.value === value)?.value;
            if (checked && targetCategory) {
                const targetCategoryArray = [...transactionCategories, targetCategory];
                setTransactionCategories(targetCategoryArray);
                return targetCategoryArray;
            }
            if (!checked && targetCategory) {
                const targetCategoryArray = transactionCategories.filter((x) => x !== targetCategory);
                setTransactionCategories(targetCategoryArray);
                return targetCategoryArray;
            }
            return [];
        },
        [transactionCategories, allCategories, setTransactionCategories],
    );

    const categories = useMemo(
        () => allCategories.map((cat) => ({ ...cat, onValueChange: defineTransactionCategories })),
        [allCategories, defineTransactionCategories],
    );

    const isCategorySearch = featureToggles.enableTransactionCategoryFilter;
    const isFreetextSearch = featureToggles.enableTransactionFreetextSearch;

    const getPayload = useCallback(() => {
        return {
            amountRange: { minAmount, maxAmount },
            freetext,
            dateRange: { fromDate, toDate },
            category: transactionCategories,
            moneyDirection,
        };
    }, [freetext, minAmount, maxAmount, fromDate, toDate, transactionCategories, moneyDirection]);

    const resetDateRange = useCallback(() => {
        setFromDate(subYears(getZonedDate(), 1));
        setToDate(getZonedDate());
    }, [setFromDate, setToDate]);

    const resetTargetCategory = useCallback(() => {
        setTransactionCategories([]);
    }, [setTransactionCategories]);

    const resetFreetext = useCallback(() => {
        setFreetext(undefined);
    }, [setFreetext]);

    const resetAmountRange = useCallback(() => {
        setMinAmount(undefined);
        setMaxAmount(undefined);
    }, [setMinAmount, setMaxAmount]);

    const resetMoneyDirection = useCallback(() => {
        setMoneyDirection('sent');
    }, [setMoneyDirection]);

    const reset = () => {
        setIsFiltering(false);
        resetFreetext();
        resetTargetCategory();
        resetDateRange();
        resetAmountRange();
        resetMoneyDirection();
    };

    const removeCategoryFromArray = useCallback(
        (category: TransactionCategory) => {
            defineTransactionCategories(false, category);
        },
        [defineTransactionCategories],
    );

    const defineMinAmount = (params: CurrencyChangeParam) => {
        setMinAmount(Number.parseFloat(params.target.value as string) || undefined);
    };
    const defineMaxAmount = (params: CurrencyChangeParam) => {
        setMaxAmount(Number.parseFloat(params.target.value as string) || undefined);
    };

    const transactionSearchPayload = useMemo(() => {
        const payload = getPayload();

        const toDate = payload?.dateRange?.toDate ?? getDateTwelveMonthsInTheFuture(payload?.dateRange?.fromDate);
        const fromDate = payload?.dateRange?.fromDate ?? getDateFromTwelveMonthsAgo(payload?.dateRange?.toDate);

        return {
            category: payload?.category ?? [],
            freetext: payload?.freetext,
            fromDate: nullableZonedDateToUtc(fromDate ?? getZonedDate()),
            toDate: nullableZonedDateToUtc(toDate ?? subYears(getZonedDate(), 1)),
            ...getSearchAmounts(payload.amountRange.minAmount, payload.amountRange.maxAmount, payload.moneyDirection),
        } as SearchTransactionsRequest;
    }, [getPayload]);

    return {
        isCategorySearch,
        isFreetextSearch,
        resetSearch: reset,
        transactionSearchPayload,
        isFilteringState,
        isDirty,
        transactionCategory: {
            data: categories,
            value: transactionCategories,
            set: defineTransactionCategories,
            reset: resetTargetCategory,
            warningMessage: categoryWarningMessage,
            setWarningMessage: setCategoryWarningMessage,
            removeFromArray: removeCategoryFromArray,
        },
        dateRange: {
            fromDate: { value: fromDate, set: (date: ZonedDate | undefined) => setFromDate(date) },
            toDate: { value: toDate, set: (date: ZonedDate | undefined) => setToDate(date) },
            reset: resetDateRange,
        },
        freetext: {
            value: freetext,
            set: setFreetext,
            reset: resetFreetext,
            warningMessage: freetextWarning,
            setWarningMessage: setFreetextWarning,
        },
        moneyDirection: {
            value: moneyDirection,
            set: setMoneyDirection,
            reset: resetMoneyDirection,
        },
        minAmount: {
            value: minAmount,
            set: defineMinAmount,
            reset: resetAmountRange,
        },
        maxAmount: {
            value: maxAmount,
            set: defineMaxAmount,
            reset: resetAmountRange,
        },
    };
};

export default useTransactionFilter;
